import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISIKO_TONE = {
  hoch:    { dot: "bg-brand-darkred", bar: "bg-brand-darkred", chip: "bg-brand-darkred/12 text-brand-darkred border-brand-darkred/30", label: "Hoch" },
  mittel:  { dot: "bg-saffron",       bar: "bg-saffron",       chip: "bg-saffron/25 text-clay-dark border-saffron/50",                 label: "Mittel" },
  niedrig: { dot: "bg-sage-dark",     bar: "bg-sage-dark",     chip: "bg-sage-dark/12 text-sage-dark border-sage-dark/30",             label: "Niedrig" },
};

export default function PkiSammelAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pflege-ki/kundenakte/sammel-audit`, { withCredentials: true });
      setData(r.data);
    } catch {
      toast.error("Sammel-Audit konnte nicht geladen werden.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5" data-testid="pki-sammel-audit">
      <div className="rounded-3xl border border-cream-300 bg-peach-50 p-6 md:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-clay">
              Pflege-KI · Sammel-Audit
            </div>
            <h2 className="font-display text-2xl md:text-[28px] text-sand-800 mt-1.5 leading-tight">
              Dokumentationsqualität aller Kunden auf einen Blick
            </h2>
            <p className="text-sand-700 text-[13.5px] md:text-sm mt-2 max-w-3xl leading-relaxed">
              Aggregierte Sicht auf alle MD-Vor-Prüfungen: wie viele Personen geprüft, welche Risiko-Verteilung,
              wo entstehen die häufigsten Lücken. Hilft beim Steuern von Schulung und QM.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="pki-sammel-reload"
            className="text-[12.5px] underline text-clay-dark hover:text-brand-darkred font-semibold"
          >
            Daten neu laden
          </button>
        </div>
      </div>

      {loading && <div className="text-sand-600 text-[13px]">lädt …</div>}

      {!loading && data && (
        <>
          {/* KPI-Zeile */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pki-sammel-kpi">
            <Kpi label="Geprüfte Kunden" value={data.kunden_anzahl} />
            <Kpi label="MD-Audits gesamt" value={data.audits_anzahl} />
            <Kpi label="Durchschnitts-Score" value={data.durchschnitts_score ?? "–"} />
            <Kpi label="Audits mit hohem Risiko" value={data.risiko_verteilung_audits?.hoch ?? 0} tone="hoch" />
          </div>

          {/* Risiko-Verteilung pro Kunde (letztes Audit) */}
          <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="pki-sammel-risiko">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
                Risiko-Verteilung pro Kunde (jeweils letztes Audit)
              </div>
              <div className="text-[12px] text-sand-600">
                Basis: {sum(data.risiko_verteilung_kunden || {})} Kunden mit Audit
              </div>
            </div>
            <StackedBar
              h={data.risiko_verteilung_kunden?.hoch || 0}
              m={data.risiko_verteilung_kunden?.mittel || 0}
              n={data.risiko_verteilung_kunden?.niedrig || 0}
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {["hoch", "mittel", "niedrig"].map((p) => {
                const c = (data.risiko_verteilung_kunden || {})[p] || 0;
                const t = RISIKO_TONE[p];
                return (
                  <span key={p} className={`inline-flex items-center gap-2 px-3 h-7 rounded-full border text-[11px] uppercase tracking-[0.18em] font-semibold ${t.chip}`}
                        data-testid={`pki-sammel-risiko-chip-${p}`}>
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} aria-hidden="true" />
                    {t.label} · {c}
                  </span>
                );
              })}
            </div>
            <div className="mt-3 text-[12px] text-sand-600 max-w-3xl leading-relaxed">
              Sachlicher Hinweis: Eine geringere Anzahl in „Hoch&ldquo; und eine höhere Anzahl in „Niedrig&ldquo;
              bedeuten verbesserte Dokumentationsqualität und ein reduziertes Prüfrisiko.
            </div>
          </div>

          {/* Top-Probleme */}
          <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="pki-sammel-top">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-3">
              Top-Probleme über alle Audits
            </div>
            {(data.top_probleme || []).length === 0 ? (
              <div className="text-sand-700 text-[13px] py-3">Noch keine Befunde aggregiert.</div>
            ) : (
              <ol className="space-y-2" data-testid="pki-sammel-top-list">
                {(data.top_probleme || []).map((p, idx) => {
                  const max = (data.top_probleme[0]?.count || 1);
                  const pct = Math.round((p.count / max) * 100);
                  return (
                    <li key={`${p.label}-${idx}`}
                        data-testid={`pki-sammel-top-row-${idx}`}
                        className="rounded-xl border border-cream-300 bg-cream-50 p-3">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">#{idx + 1}</span>
                          <span className="text-[13.5px] font-semibold text-sand-800 truncate">{p.label}</span>
                          {p.kind === "freitext" && (
                            <span className="text-[10.5px] text-sand-500 uppercase tracking-[0.18em]">· Freitext</span>
                          )}
                        </div>
                        <span className="font-mono text-[13px] text-sand-800">{p.count}×</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden">
                        <div className="h-full bg-brand-darkred/70" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="mt-3 text-[12px] text-sand-600 max-w-3xl leading-relaxed">
              Konzentriere Schulung und QM-Audits zuerst auf die obersten Punkte – das erhöht die
              Vollständigkeit der Dokumentation am schnellsten.
            </div>
          </div>

          {/* Letzte Audits pro Kunde */}
          <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="pki-sammel-letzte">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-3">
              Letztes Audit pro Kunde · {data.letzte_audits_pro_kunde?.length || 0} Eintrag/Einträge
            </div>
            {(data.letzte_audits_pro_kunde || []).length === 0 ? (
              <div className="text-sand-700 text-[13px] py-3">Noch keine Audits.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-left text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 border-b border-cream-300">
                      <th className="py-2 pr-3">Kunde</th>
                      <th className="py-2 pr-3">Datum</th>
                      <th className="py-2 pr-3">Score</th>
                      <th className="py-2 pr-3">Risiko</th>
                      <th className="py-2 pr-3">Hoch</th>
                      <th className="py-2 pr-3">Mittel</th>
                      <th className="py-2 pr-3">Niedrig</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.letzte_audits_pro_kunde || []).map((a) => {
                      const t = RISIKO_TONE[a.label] || null;
                      return (
                        <tr key={a.customer_label} className="border-b border-cream-100 last:border-0">
                          <td className="py-2 pr-3 text-sand-800">{a.customer_label}</td>
                          <td className="py-2 pr-3 text-sand-700 whitespace-nowrap">{fmtDateLong(a.created_at)}</td>
                          <td className="py-2 pr-3 font-mono text-sand-800">{a.score ?? "–"}</td>
                          <td className="py-2 pr-3">
                            {t ? (
                              <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full border text-[10.5px] uppercase tracking-[0.18em] font-semibold ${t.chip}`}>
                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} aria-hidden="true" />
                                {t.label}
                              </span>
                            ) : "–"}
                          </td>
                          <td className="py-2 pr-3 font-mono text-brand-darkred">{a.hoch ?? 0}</td>
                          <td className="py-2 pr-3 font-mono text-clay-dark">{a.mittel ?? 0}</td>
                          <td className="py-2 pr-3 font-mono text-sage-dark">{a.niedrig ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const t = tone ? RISIKO_TONE[tone] : null;
  return (
    <div className={`rounded-2xl border p-4 ${t ? "border-brand-darkred/30 bg-brand-darkred/5" : "border-cream-300 bg-white"}`}>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-sand-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl leading-tight mt-1 ${t ? "text-brand-darkred" : "text-sand-800"}`}>{value}</div>
    </div>
  );
}

function StackedBar({ h, m, n }) {
  const total = h + m + n;
  if (!total) {
    return <div className="h-3 rounded-full bg-cream-200" />;
  }
  return (
    <div className="h-3 w-full rounded-full bg-cream-200 overflow-hidden flex" data-testid="pki-sammel-stacked-bar">
      <div className="bg-brand-darkred" style={{ width: `${(h / total) * 100}%` }} title={`Hoch: ${h}`} />
      <div className="bg-saffron"       style={{ width: `${(m / total) * 100}%` }} title={`Mittel: ${m}`} />
      <div className="bg-sage-dark"     style={{ width: `${(n / total) * 100}%` }} title={`Niedrig: ${n}`} />
    </div>
  );
}

function sum(obj) {
  return Object.values(obj || {}).reduce((a, b) => a + (Number(b) || 0), 0);
}

function fmtDateLong(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
